import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { Bar, Line, Doughnut } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
)

export default function Analytics() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState('30') // days
  const [analytics, setAnalytics] = useState({
    assessmentsByDay: [],
    assessmentsByRecommendation: {},
    contractorGrowth: [],
    revenueData: [],
    topContractors: [],
  })

  useEffect(() => {
    // Check if user is admin
    if (user && user.role !== 'admin') {
      navigate('/dashboard')
      return
    }

    if (user) {
      fetchAnalytics()
    }
  }, [user, navigate, timeRange])

  const fetchAnalytics = async () => {
    try {
      setLoading(true)

      // Calculate date range
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - parseInt(timeRange))

      // Assessments by day
      const { data: assessments, error: assessmentsError } = await supabase
        .from('assessments')
        .select('created_at, recommendation, contractor_id')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true })

      if (assessmentsError) throw assessmentsError

      // Process assessments by day
      const assessmentsByDay = processAssessmentsByDay(assessments || [], parseInt(timeRange))

      // Assessments by recommendation
      const assessmentsByRecommendation = {
        replace: assessments?.filter(a => a.recommendation === 'replace').length || 0,
        consider: assessments?.filter(a => a.recommendation === 'consider').length || 0,
        repair: assessments?.filter(a => a.recommendation === 'repair').length || 0,
        maintain: assessments?.filter(a => a.recommendation === 'maintain').length || 0,
      }

      // Contractor growth over time
      const { data: contractors, error: contractorsError } = await supabase
        .from('contractors')
        .select('created_at, subscription_status')
        .order('created_at', { ascending: true })

      if (contractorsError) throw contractorsError

      const contractorGrowth = processContractorGrowth(contractors || [], parseInt(timeRange))

      // Revenue calculation
      const { data: activeContractors, error: revenueError } = await supabase
        .from('contractors')
        .select('subscription_status, assessments_this_period, included_assessments, created_at')
        .in('subscription_status', ['active', 'trialing'])

      if (revenueError) throw revenueError

      const revenueData = calculateRevenue(activeContractors || [])

      // Top contractors by assessments
      const { data: topContractorsData, error: topError } = await supabase
        .from('contractors')
        .select('id, company_name, assessments_this_period')
        .order('assessments_this_period', { ascending: false })
        .limit(10)

      if (topError) throw topError

      setAnalytics({
        assessmentsByDay,
        assessmentsByRecommendation,
        contractorGrowth,
        revenueData,
        topContractors: topContractorsData || [],
      })
    } catch (error) {
      console.error('Error fetching analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  const processAssessmentsByDay = (assessments, days) => {
    const dayMap = {}
    const endDate = new Date()

    // Initialize all days with 0
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date()
      date.setDate(endDate.getDate() - i)
      const dateKey = date.toISOString().split('T')[0]
      dayMap[dateKey] = 0
    }

    // Count assessments per day
    assessments.forEach(assessment => {
      const dateKey = new Date(assessment.created_at).toISOString().split('T')[0]
      if (dayMap.hasOwnProperty(dateKey)) {
        dayMap[dateKey]++
      }
    })

    return Object.entries(dayMap).map(([date, count]) => ({ date, count }))
  }

  const processContractorGrowth = (contractors, days) => {
    const dayMap = {}
    const endDate = new Date()

    // Initialize all days with 0
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date()
      date.setDate(endDate.getDate() - i)
      const dateKey = date.toISOString().split('T')[0]
      dayMap[dateKey] = 0
    }

    // Count cumulative contractors
    let cumulative = 0
    const sortedContractors = contractors.sort((a, b) =>
      new Date(a.created_at) - new Date(b.created_at)
    )

    Object.keys(dayMap).forEach(dateKey => {
      const signupsOnDay = sortedContractors.filter(c =>
        new Date(c.created_at).toISOString().split('T')[0] === dateKey
      ).length
      cumulative += signupsOnDay
      dayMap[dateKey] = cumulative
    })

    return Object.entries(dayMap).map(([date, count]) => ({ date, count }))
  }

  const calculateRevenue = (contractors) => {
    const baseRevenue = contractors.filter(c => c.subscription_status === 'active').length * 1299
    const overageRevenue = contractors.reduce((sum, c) => {
      const overage = Math.max(0, c.assessments_this_period - c.included_assessments)
      return sum + (overage * 0.39)
    }, 0)

    return {
      base: baseRevenue,
      overage: overageRevenue,
      total: baseRevenue + overageRevenue,
    }
  }

  const assessmentsChartData = {
    labels: analytics.assessmentsByDay.map(d => new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
    datasets: [
      {
        label: 'Assessments',
        data: analytics.assessmentsByDay.map(d => d.count),
        backgroundColor: 'rgba(99, 102, 241, 0.5)',
        borderColor: 'rgb(99, 102, 241)',
        borderWidth: 2,
      },
    ],
  }

  const contractorGrowthData = {
    labels: analytics.contractorGrowth.map(d => new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
    datasets: [
      {
        label: 'Total Contractors',
        data: analytics.contractorGrowth.map(d => d.count),
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 2,
        fill: true,
      },
    ],
  }

  const recommendationData = {
    labels: ['Replace', 'Consider', 'Repair', 'Maintain'],
    datasets: [
      {
        data: [
          analytics.assessmentsByRecommendation.replace,
          analytics.assessmentsByRecommendation.consider,
          analytics.assessmentsByRecommendation.repair,
          analytics.assessmentsByRecommendation.maintain,
        ],
        backgroundColor: [
          'rgba(239, 68, 68, 0.7)',
          'rgba(245, 158, 11, 0.7)',
          'rgba(34, 197, 94, 0.7)',
          'rgba(59, 130, 246, 0.7)',
        ],
        borderWidth: 0,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  }

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
      },
    },
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-xl text-gray-600">Loading analytics...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Platform Analytics</h1>
          <p className="text-gray-600">Usage metrics, trends, and performance insights</p>
          <div className="mt-4 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
            >
              ← Back to Admin
            </button>
            <div>
              <label className="text-sm font-medium text-gray-700 mr-2">Time Range:</label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="60">Last 60 Days</option>
                <option value="90">Last 90 Days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Revenue Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-sm text-gray-600 mb-1">Base Subscription Revenue</div>
            <div className="text-3xl font-bold text-indigo-600">
              ${analytics.revenueData.base?.toLocaleString() || 0}
            </div>
            <div className="text-sm text-gray-500 mt-1">Monthly recurring</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-sm text-gray-600 mb-1">Overage Revenue</div>
            <div className="text-3xl font-bold text-purple-600">
              ${analytics.revenueData.overage?.toFixed(2) || 0}
            </div>
            <div className="text-sm text-gray-500 mt-1">Metered usage</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-sm text-gray-600 mb-1">Total Revenue</div>
            <div className="text-3xl font-bold text-green-600">
              ${analytics.revenueData.total?.toLocaleString() || 0}
            </div>
            <div className="text-sm text-gray-500 mt-1">This period</div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Assessments by Day */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Assessments Over Time</h2>
            <div className="h-64">
              <Bar data={assessmentsChartData} options={chartOptions} />
            </div>
          </div>

          {/* Contractor Growth */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Contractor Growth</h2>
            <div className="h-64">
              <Line data={contractorGrowthData} options={chartOptions} />
            </div>
          </div>

          {/* Recommendations Distribution */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Assessment Recommendations</h2>
            <div className="h-64">
              <Doughnut data={recommendationData} options={doughnutOptions} />
            </div>
          </div>

          {/* Top Contractors */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Top Contractors by Usage</h2>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {analytics.topContractors.map((contractor, index) => (
                <div key={contractor.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold mr-3">
                      {index + 1}
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {contractor.company_name}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-gray-600">
                    {contractor.assessments_this_period} assessments
                  </div>
                </div>
              ))}
              {analytics.topContractors.length === 0 && (
                <div className="text-center text-gray-500 py-8">No data available</div>
              )}
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Assessment Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {analytics.assessmentsByRecommendation.replace}
              </div>
              <div className="text-sm text-red-700 mt-1">Replace</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {analytics.assessmentsByRecommendation.consider}
              </div>
              <div className="text-sm text-yellow-700 mt-1">Consider</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {analytics.assessmentsByRecommendation.repair}
              </div>
              <div className="text-sm text-green-700 mt-1">Repair</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {analytics.assessmentsByRecommendation.maintain}
              </div>
              <div className="text-sm text-blue-700 mt-1">Maintain</div>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-8 bg-indigo-50 border border-indigo-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-indigo-900 mb-2">Analytics Insights</h3>
          <ul className="text-sm text-indigo-800 space-y-1">
            <li>• Track platform growth and usage trends over time</li>
            <li>• Monitor revenue from subscriptions and metered billing</li>
            <li>• Identify top-performing contractors and usage patterns</li>
            <li>• Analyze assessment recommendations for quality insights</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
